/**
 * @author: subhasish.ghosh@zoom.us
 */

import {
  AppBar,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Toolbar,
} from "@mui/material";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ACTION_TYPE, SIDEBAR_ITEMS, TABLE_TYPES_KEYS } from "@utils/constants";

// Keys for the tabs you want to hide from Admin
const EXCLUDED_ADMIN_TAB_KEYS = new Set([
  "chargeback",           // Customer Disputes
  "bizOperationIndirect", // Billing Ops Cancel (Indirect)
  "nonRenewalIndirect",   // Non-Renewal Cancel (Indirect)
  "productCatalogue",     // Product Catalogue
]);

const TopBar = ({
  actionTypes,
  setSelectedActionType,
  setSelectedTableTab,
  isAdmin,
}) => {
  const allowedActionTypes = useMemo(
    () => {
      const acc = {};
      if (isAdmin) {
        // 1. Force Admin to be the FIRST tab
        if (SIDEBAR_ITEMS.admin) {
          acc.admin = SIDEBAR_ITEMS.admin;
        }

        // 2. Append all other tabs EXCEPT the excluded ones
        Object.keys(SIDEBAR_ITEMS).forEach((key) => {
          if (key !== "admin" && !EXCLUDED_ADMIN_TAB_KEYS.has(key)) {
            acc[key] = SIDEBAR_ITEMS[key];
          }
        });
        return acc;
      }
      // For non-admin users, filter by their assigned actionTypes
      actionTypes.forEach((key) => {
        if (SIDEBAR_ITEMS[key]) {
          acc[key] = SIDEBAR_ITEMS[key];
        }
      });
      return acc;
    },
    [actionTypes, isAdmin]
  );

  const [urlSearchParams, setURLSearchParams] = useSearchParams();
  // const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const [current, setCurrent] = useState("");

  useEffect(() => {
    if (current && current !== null) {
      urlSearchParams.set("actionType", current);
      setURLSearchParams(urlSearchParams);
    }
  }, [current, setURLSearchParams]);

  useEffect(() => {
    const searchParamActionType = urlSearchParams.get(ACTION_TYPE);

    // handle selected action type
    if (
      searchParamActionType &&
      Object.keys(allowedActionTypes).includes(searchParamActionType)
    ) {
      setCurrent(searchParamActionType);
      setSelectedActionType(searchParamActionType);
    } else if (
      actionTypes ||
      (actionTypes?.length === 1 && actionTypes[0] !== "")
    ) {
      // incase no action types are provided in uri
      const referenceList = Object.keys(allowedActionTypes);
      const referenceMap = new Map(
        referenceList.map((item, index) => [item, index])
      );
      actionTypes.sort((a, b) => referenceMap.get(a) - referenceMap.get(b));

      const firstSelected = Object.keys(allowedActionTypes)[0];
      setCurrent(firstSelected);
      setSelectedActionType(firstSelected);
    }
  }, [actionTypes, allowedActionTypes, urlSearchParams, setSelectedActionType]);

  return (
    <Box className="dashboard-top-bar-container">
      <AppBar
        className="dashboard-top-bar"
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar className="dashboard-toolbar">
          <Box className="dashboard-toolbar-container">
            <List
              className="top-bar-list"
              sx={{
                flexDirection: "row",
                padding: 0,
              }}
            >
              {Object.keys(allowedActionTypes).map((key, index) => (
                <ListItem
                  key={index}
                  disablePadding
                  style={{ width: "fit-content" }}
                >
                  <ListItemButton
                    className={classNames(
                      "sidebar_item",
                      current === key ? "sidebar_item_selected" : ""
                    )}
                    onClick={() => {
                      if (current) {
                        setCurrent(key);
                        setSelectedActionType(key);
                        // reset table tab
                        setSelectedTableTab(TABLE_TYPES_KEYS[0]);
                      }
                    }}
                  >
                    {current ? (
                      <ListItemText
                        className="dashboard-topbar-text"
                        primary={allowedActionTypes[key]}
                      />
                    ) : (
                      <Skeleton
                        animation="wave"
                        sx={{ bgcolor: "#f6f6f6" }}
                        style={{
                          height: "32px",
                          maxWidth: "180px",
                          minWidth: "120px",
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default TopBar;
